import { useState, useCallback, useEffect, useRef } from 'react';
import { apiFetch } from '../../lib/api';
import { messageForFailedResponse } from '../../lib/formatApiError';
import { warehouseOptionLabel } from '@/lib/warehouseLabel';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { machineDetailPath, machinePicklistPath } from '@/lib/entityPaths';
import { parseGeneratedPicklistId } from '@/lib/machinePicklist';
import { useAnyPermission } from '../../hooks/usePermission';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { PicklistMobileView, type PicklistLineView } from '@/components/picklist';

interface PicklistData {
  id: string;
  machineId: string;
  machineName: string;
  warehouseId: string;
  warehouseName?: string;
  warehouseType?: string;
  venueName?: string;
  refillSessionId: string | null;
  generatedAt: string;
  bufferPercent: number;
  lines: PicklistLineView[];
  totalItemsToPick: number;
  totalPickWeightKg?: number;
}

interface WarehouseOption {
  id: string;
  name: string;
  type: string;
  parentWarehouseId?: string | null;
}

function sortWarehouseOptions(items: WarehouseOption[]): WarehouseOption[] {
  return [...items].sort((a, b) => {
    if (a.type === 'HUB' && b.type !== 'HUB') return -1;
    if (b.type === 'HUB' && a.type !== 'HUB') return 1;
    return a.name.localeCompare(b.name);
  });
}

function resolveDefaultPickWarehouseId(
  machineSpokeId: string | null | undefined,
  warehouses: WarehouseOption[]
): string | undefined {
  if (warehouses.length === 0) return undefined;

  const hubs = warehouses.filter((w) => w.type === 'HUB');
  if (machineSpokeId) {
    const spoke = warehouses.find((w) => w.id === machineSpokeId);
    if (spoke?.parentWarehouseId) {
      const parentHub = hubs.find((w) => w.id === spoke.parentWarehouseId);
      if (parentHub) return parentHub.id;
    }
  }

  return hubs[0]?.id;
}

export function MachinePicklist() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedPicklistId = searchParams.get('picklistId')?.trim() || null;
  const [picklist, setPicklist] = useState<PicklistData | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [pickingLineId, setPickingLineId] = useState<string | null>(null);
  const [deferringLineId, setDeferringLineId] = useState<string | null>(null);
  const [startingRefill, setStartingRefill] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncingStock, setSyncingStock] = useState(false);
  const canSyncCloudStock = useAnyPermission(['machines.write', 'picklists.write']);
  const cloudStockSyncedRef = useRef<string | null>(null);
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [machineSpokeId, setMachineSpokeId] = useState<string | null>(null);
  const [unmanagedMachine, setUnmanagedMachine] = useState(false);

  const defaultPickWarehouseId = resolveDefaultPickWarehouseId(machineSpokeId, warehouses);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [warehousesRes, machineRes] = await Promise.all([
          apiFetch('/api/v1/warehouses?isActive=true&pageSize=100'),
          id ? apiFetch(`/api/v1/machines/${id}`) : Promise.resolve(null),
        ]);
        if (cancelled) return;

        if (warehousesRes.ok) {
          const json = await warehousesRes.json();
          const items = (json.items ?? []) as Array<{
            id: string;
            name: string;
            type: string;
            parentWarehouseId?: string | null;
            parentWarehouse?: { id: string } | null;
          }>;
          setWarehouses(
            sortWarehouseOptions(
              items.map((w) => ({
                id: w.id,
                name: w.name,
                type: w.type,
                parentWarehouseId: w.parentWarehouseId ?? w.parentWarehouse?.id ?? null,
              }))
            )
          );
        }

        if (machineRes?.ok) {
          const payload = await machineRes.json();
          const spokeId =
            typeof payload?.machine?.spokeId === 'string'
              ? payload.machine.spokeId
              : typeof payload?.spokeId === 'string'
                ? payload.spokeId
                : null;
          setMachineSpokeId(spokeId);
          setUnmanagedMachine(payload?.machine?.lifecycleStatus === 'UNMANAGED');
        }
      } catch (err) {
        console.error('Error loading picklist context:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const refreshOpenPicklist = useCallback(async (current: PicklistData): Promise<PicklistData> => {
    if (current.refillSessionId) return current;
    const refresh = await apiFetch(`/api/v1/picklists/${current.id}/refresh`, { method: 'POST' });
    if (!refresh.ok) {
      throw new Error(
        await messageForFailedResponse(refresh, 'Stock synced, but the picklist could not be refreshed')
      );
    }
    return (await refresh.json()) as PicklistData;
  }, []);

  const syncCloudStock = useCallback(async (): Promise<boolean> => {
    if (!id) return false;
    setSyncingStock(true);
    setError(null);
    try {
      const response = await apiFetch(`/api/v1/machines/${id}/cloud-stock`, { method: 'POST' });
      if (!response.ok) {
        throw new Error(await messageForFailedResponse(response, 'Failed to sync stock from Cloud'));
      }
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync stock from Cloud');
      return false;
    } finally {
      setSyncingStock(false);
    }
  }, [id]);

  const loadPicklist = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    let stockSynced = false;
    if (canSyncCloudStock && cloudStockSyncedRef.current !== id) {
      cloudStockSyncedRef.current = id;
      stockSynced = await syncCloudStock();
    }
    const withFreshStock = async (current: PicklistData): Promise<PicklistData> => {
      if (!stockSynced) return current;
      try {
        return await refreshOpenPicklist(current);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Stock synced, but the picklist could not be refreshed'
        );
        return current;
      }
    };
    try {
      if (requestedPicklistId) {
        const detailRes = await apiFetch(
          `/api/v1/picklists/${encodeURIComponent(requestedPicklistId)}`
        );
        if (!detailRes.ok) {
          setPicklist(null);
          setError(await messageForFailedResponse(detailRes, 'Picklist not found'));
          return;
        }
        const data = (await detailRes.json()) as PicklistData;
        if (typeof data.machineId !== 'string' || data.machineId !== id) {
          setPicklist(null);
          setError('This picklist does not belong to this machine');
          return;
        }
        setPicklist(await withFreshStock(data));
        return;
      }

      const listRes = await apiFetch(
        `/api/v1/picklists?machineId=${encodeURIComponent(id)}&pageSize=1&page=1`
      );
      if (!listRes.ok) {
        setPicklist(null);
        return;
      }
      const listJson = await listRes.json();
      const items = listJson.items as Array<{ id: string }> | undefined;
      if (!items?.length) {
        setPicklist(null);
        return;
      }
      const detailRes = await apiFetch(`/api/v1/picklists/${items[0].id}`);
      if (!detailRes.ok) {
        setPicklist(null);
        return;
      }
      setPicklist(await withFreshStock((await detailRes.json()) as PicklistData));
    } catch (err) {
      console.error('Error loading picklist:', err);
      setPicklist(null);
    } finally {
      setLoading(false);
    }
  }, [id, requestedPicklistId, canSyncCloudStock, syncCloudStock, refreshOpenPicklist]);

  useEffect(() => {
    void loadPicklist();
  }, [loadPicklist]);

  const generatePicklist = useCallback(
    async (warehouseId?: string) => {
      if (!id) return;
      setGenerating(true);
      setError(null);
      try {
        const pickWarehouseId = warehouseId ?? defaultPickWarehouseId;
        const response = await apiFetch('/api/v1/picklists/generate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            machineId: id,
            bufferPercent: 10,
            ...(pickWarehouseId ? { warehouseId: pickWarehouseId } : {}),
          }),
        });
        if (!response.ok) {
          throw new Error(await messageForFailedResponse(response, 'Failed to generate picklist'));
        }
        const created = (await response.json()) as PicklistData;
        const createdId = parseGeneratedPicklistId(created);
        setPicklist(created);
        if (createdId !== requestedPicklistId) {
          navigate(machinePicklistPath(id, createdId), { replace: true });
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to generate picklist');
      } finally {
        setGenerating(false);
      }
    },
    [id, defaultPickWarehouseId, requestedPicklistId, navigate]
  );

  const syncStockFromCloud = useCallback(async () => {
    const synced = await syncCloudStock();
    if (!synced || !picklist || picklist.refillSessionId) return;
    try {
      setPicklist(await refreshOpenPicklist(picklist));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Stock synced, but the picklist could not be refreshed'
      );
    }
  }, [syncCloudStock, picklist, refreshOpenPicklist]);

  const alignedHubMachineIdRef = useRef<string | null>(null);
  useEffect(() => {
    alignedHubMachineIdRef.current = null;
  }, [id]);

  useEffect(() => {
    if (!id || loading || generating || unmanagedMachine || !defaultPickWarehouseId) return;
    if (requestedPicklistId) return;
    if (alignedHubMachineIdRef.current === id) return;

    if (!picklist) {
      alignedHubMachineIdRef.current = id;
      return;
    }

    const pickingStarted = picklist.lines.some((line) => (line.quantityPicked ?? 0) > 0);
    const locked = Boolean(picklist.refillSessionId) || pickingStarted;
    if (locked || picklist.warehouseId === defaultPickWarehouseId) {
      alignedHubMachineIdRef.current = id;
      return;
    }

    alignedHubMachineIdRef.current = id;
    void generatePicklist(defaultPickWarehouseId);
  }, [id, loading, generating, unmanagedMachine, defaultPickWarehouseId, picklist, generatePicklist, requestedPicklistId]);

  const handlePick = useCallback(
    async (lineId: string, quantity: number) => {
      if (!picklist) return;
      setPickingLineId(lineId);
      setError(null);
      try {
        const res = await apiFetch(
          `/api/v1/picklists/${picklist.id}/lines/${lineId}/pick`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ quantityPicked: quantity }),
          }
        );
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'Pick failed');
        }
        const detailRes = await apiFetch(`/api/v1/picklists/${picklist.id}`);
        if (detailRes.ok) {
          setPicklist(await detailRes.json());
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Pick failed');
      } finally {
        setPickingLineId(null);
      }
    },
    [picklist]
  );

  const handleDefer = useCallback(
    async (lineId: string, deferred: boolean) => {
      if (!picklist) return;
      setDeferringLineId(lineId);
      setError(null);
      try {
        const res = await apiFetch(
          `/api/v1/picklists/${picklist.id}/lines/${lineId}/defer`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ deferred }),
          }
        );
        if (!res.ok) {
          throw new Error(await messageForFailedResponse(res, 'Failed to update deferral'));
        }
        setPicklist(await res.json());
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update deferral');
      } finally {
        setDeferringLineId(null);
      }
    },
    [picklist]
  );

  const completePicking = useCallback(async () => {
    if (!picklist || !id) return;
    setStartingRefill(true);
    setError(null);
    try {
      const createRes = await apiFetch('/api/v1/refill-sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ machineId: id, picklistId: picklist.id }),
      });
      if (!createRes.ok) {
        const data = await createRes.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to create refill session');
      }
      const session = await createRes.json();
      const dispatchRes = await apiFetch(`/api/v1/refill-sessions/${session.id}/dispatch`, {
        method: 'PATCH',
      });
      if (!dispatchRes.ok) {
        const data = await dispatchRes.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to dispatch refill session');
      }
      navigate(`/refill-sessions/${session.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete picking');
    } finally {
      setStartingRefill(false);
    }
  }, [picklist, id, navigate]);

  const pickedCount = picklist?.lines.filter((l) => l.isPicked).length ?? 0;
  const deferredCount = picklist?.lines.filter((l) => l.isDeferred && !l.isPicked).length ?? 0;
  const totalLines = picklist?.lines.length ?? 0;
  const openCount = picklist?.lines.filter((l) => !l.isPicked && !l.isDeferred).length ?? 0;
  const pickedUnits =
    picklist?.lines.reduce((sum, line) => sum + (line.quantityPicked ?? 0), 0) ?? 0;
  const allResolved = totalLines > 0 && openCount === 0;
  const canComplete = allResolved && pickedUnits > 0;
  // A picklist already linked to a refill session is read-only history.
  const isConsumed = !!picklist?.refillSessionId;
  const warehouseLocked = pickedCount > 0 || isConsumed;
  const selectedWarehouseId = warehouseLocked
    ? picklist?.warehouseId
    : (defaultPickWarehouseId ?? picklist?.warehouseId);
  const selectedWarehouse = warehouses.find((w) => w.id === selectedWarehouseId);

  if (loading || generating) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">
        {generating ? 'Updating picklist…' : syncingStock ? 'Syncing stock…' : 'Loading…'}
      </div>
    );
  }

  if (unmanagedMachine) {
    return (
      <div className="min-h-screen bg-background mx-auto w-full max-w-lg px-4 py-10 space-y-4">
        <Link
          to={id ? machineDetailPath(id, 'picklists') : '/machines'}
          className="text-sm text-muted-foreground hover:underline"
        >
          ← Machine
        </Link>
        <p className="text-sm text-muted-foreground">
          This machine is unmanaged in Express. Set a Cloud lifecycle on Edit to pick and refill it.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background mx-auto w-full max-w-lg lg:max-w-6xl">
      <header className="sticky top-0 z-10 bg-background border-b px-4 py-3">
        <div className="flex items-center gap-3">
          <Link
            to={id ? machineDetailPath(id, 'picklists') : '/machines'}
            className="shrink-0 text-sm text-muted-foreground hover:underline"
          >
            ← {picklist?.machineName || 'Machine'}
          </Link>
          {selectedWarehouseId ? (
            <div className="flex min-w-0 items-center gap-2">
              <span className="shrink-0 text-xs text-muted-foreground">Picking from</span>
              <Select
                value={selectedWarehouseId}
                onValueChange={(warehouseId) => {
                  if (warehouseId !== selectedWarehouseId) {
                    generatePicklist(warehouseId);
                  }
                }}
                disabled={warehouseLocked || generating}
              >
                <SelectTrigger className="h-8 w-auto min-w-[12rem] max-w-[16rem] text-sm">
                  <SelectValue placeholder="Select warehouse">
                    {selectedWarehouse
                      ? warehouseOptionLabel(selectedWarehouse)
                      : (picklist?.warehouseName ?? 'Warehouse')}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      {warehouseOptionLabel(w)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {warehouseLocked && !isConsumed ? (
                <span className="truncate text-xs text-muted-foreground">
                  Warehouse locked once picking has started
                </span>
              ) : null}
            </div>
          ) : null}
          <div className="ml-auto flex shrink-0 items-center gap-2">
            {canSyncCloudStock ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8"
                disabled={syncingStock || generating}
                onClick={() => void syncStockFromCloud()}
              >
                {syncingStock ? 'Syncing stock…' : 'Sync stock'}
              </Button>
            ) : null}
            {picklist && (
              <span className="text-sm font-medium">
                {pickedCount}/{totalLines} picked
                {deferredCount > 0 ? ` · ${deferredCount} deferred` : ''}
              </span>
            )}
          </div>
        </div>
      </header>

      <main className="px-4 py-4 pb-24">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-4 text-sm">
            {error}
          </div>
        )}

        {!picklist ? (
          <Card>
            <CardContent className="py-10 text-center space-y-4">
              <p className="text-muted-foreground text-sm">No picklist for this machine yet.</p>
              <Button
                onClick={() => generatePicklist(selectedWarehouseId)}
                disabled={generating}
              >
                Generate picklist
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {isConsumed && (
              <p className="mb-3 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
                This picklist has been handed to a refill session and is read-only. Generate the
                next list from the machine{' '}
                <Link to={id ? machineDetailPath(id, 'picklists') : '/machines'} className="underline">
                  Picklists
                </Link>{' '}
                tab.
              </p>
            )}
            <PicklistMobileView
              lines={picklist.lines}
              onPick={handlePick}
              pickingLineId={pickingLineId}
              onDefer={handleDefer}
              deferringLineId={deferringLineId}
              readOnly={isConsumed}
            />
            {picklist.totalPickWeightKg != null && picklist.totalPickWeightKg > 0 && (
              <p className="text-sm text-muted-foreground mt-4 text-center">
                Est. weight: {picklist.totalPickWeightKg} kg
              </p>
            )}
          </>
        )}
      </main>

      {picklist && (
        <footer className="fixed bottom-0 left-0 right-0 z-20 border-t bg-background p-4 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]">
          <div className="mx-auto w-full max-w-lg">
            {isConsumed ? (
              <Button
                className="w-full"
                variant="outline"
                onClick={() => navigate(`/refill-sessions/${picklist.refillSessionId}`)}
              >
                View refill session
              </Button>
            ) : (
              <>
                <Button
                  className="w-full"
                  disabled={!canComplete || startingRefill}
                  onClick={completePicking}
                >
                  {startingRefill ? 'Completing…' : 'Complete picking — load van'}
                </Button>
                {!allResolved && (
                  <p className="text-xs text-center text-muted-foreground mt-2">
                    Pick or defer remaining lines before completing
                  </p>
                )}
                {allResolved && pickedUnits === 0 && (
                  <p className="text-xs text-center text-muted-foreground mt-2">
                    All products were deferred. Nothing to load this visit.
                  </p>
                )}
              </>
            )}
          </div>
        </footer>
      )}
    </div>
  );
}

export default MachinePicklist;
